import { BorderRadius, Color, Spacing, Typography } from '@persona-id/design-tokens';
import { kebabCase } from 'lodash/fp';
import { FC, useRef } from 'react';
import styled from 'styled-components';

import { WORKFLOW_STEP_CARD_WIDTH } from 'dashboard/apps/InquiryTemplateEditor/WorkflowTree/WorkflowStepCard';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

const Card = styled.div<{
  active: boolean;
  hasError: boolean;
  wasChosenDuringRun: boolean;
  showingWorkflowRun: boolean;
}>`
  max-width: ${WORKFLOW_STEP_CARD_WIDTH}px;
  height: 32px;
  border: 2px solid;
  border-radius: ${BorderRadius.Lg};
  box-shadow: ${Elevation.Mid};
  display: inline-flex;
  align-items: center;
  padding: ${Spacing[2]};
  background-color: ${Color.PrimaryWhite};
  cursor: pointer;
  user-select: none;
  border-color: ${({ active, hasError, wasChosenDuringRun }) => {
    // Do we need to handle error cases here?
    if (hasError) {
      return Color.CoreRed800;
    } else if (active) {
      return Color.CorePurple800;
    } else if (wasChosenDuringRun) {
      return Color.CorePurple600;
    } else {
      return Color.CorePurple200;
    }
  }};
  opacity: ${({ showingWorkflowRun, wasChosenDuringRun }) =>
    showingWorkflowRun && !wasChosenDuringRun ? 0.4 : 1};
`;

const RouteNumber = styled.div`
  ${Typography.TextExtraSmall}
  height: 16px;
  min-width: 16px;
  line-height: 16px;
  background-color: ${Color.CoreSlate100};
  border-radius: ${BorderRadius.Md};
  text-align: center;
`;

const TitleLabel = styled.div`
  ${Typography.TextMediumBold}
  color: ${Color.TextPrimary};
  flex: 1;
  margin-left: 4px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

interface WorkflowStepConditionalPillProps {
  title: string;
  routeIndex: number;
  active?: boolean;
  hasError?: boolean;
  wasChosenDuringRun?: boolean;
  showingWorkflowRun?: boolean;
  onClickCard: () => void;
}

const WorkflowStepConditionalPill: FC<WorkflowStepConditionalPillProps> = ({
  title,
  routeIndex,
  active = false,
  hasError = false,
  wasChosenDuringRun = false,
  showingWorkflowRun = false,
  onClickCard,
}) => {
  const titleElement = useRef<HTMLDivElement>(null);

  return (
    <Tooltip
      disabled={
        // Is TitleLabel not truncated?
        (titleElement?.current?.clientWidth ?? 0) >= (titleElement?.current?.scrollWidth ?? 0)
      }
      side='top'
      trigger={
        <Card
          active={active}
          hasError={hasError}
          id={`workflow-step-conditional-pill__card-${routeIndex}-${kebabCase(title)}`}
          role='button'
          showingWorkflowRun={showingWorkflowRun}
          wasChosenDuringRun={wasChosenDuringRun}
          onClick={onClickCard}
        >
          <RouteNumber>{routeIndex}</RouteNumber>
          <TitleLabel ref={titleElement}>{title}</TitleLabel>
        </Card>
      }
    >
      <Paragraph size='md'>{title}</Paragraph>
    </Tooltip>
  );
};

export default WorkflowStepConditionalPill;
